import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select-field';
import { SelectItem } from '@/components/ui/select';
import { Genre } from '@/domain/clients/client';
import { CreateClientDto } from '@/domain/clients/client-schemas';
import { getDocTypeOptions } from '@/domain/shared';
import { UseFormReturn } from 'react-hook-form';
import { argStates } from '../collections/arg-states';
import { countries } from '../collections/countries';
import DateSelector from '../shared/DateSelector';

interface TitularStepProps {
  form: UseFormReturn<CreateClientDto>;
}

export const TitularStep = ({ form }: TitularStepProps) => {
  return (
    <>
      {/* Identity */}
      <FormField
        control={form.control}
        name="userData.legalName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Apellido, Nombre *</FormLabel>
            <FormControl>
              <Input placeholder="Pérez, Juan" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.docType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de Documento *</FormLabel>
            <SelectField
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Seleccionar tipo de documento"
              triggerRef={field.ref}
            >
              {getDocTypeOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectField>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.docNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Número de Documento *</FormLabel>
            <FormControl>
              <Input placeholder="12345678" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Personal */}
      <FormField
        control={form.control}
        name="userData.genre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Género *</FormLabel>
            <SelectField
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Seleccionar género"
              triggerRef={field.ref}
            >
              {Object.entries(Genre).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectField>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.dateOfBirth"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fecha de Nacimiento</FormLabel>
            <FormControl>
              <DateSelector
                initialDate={field.value ?? undefined}
                onDateSelected={(date: Date) => field.onChange(date)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Contact */}
      <FormField
        control={form.control}
        name="userData.email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input placeholder="juan@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Teléfono de Contacto *</FormLabel>
            <FormControl>
              <Input placeholder="+54 9 11 1234-5678" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Address */}
      <FormField
        control={form.control}
        name="userData.street"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Dirección *</FormLabel>
            <FormControl>
              <Input placeholder="Av. Principal 123" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.country"
        render={({ field }) => (
          <FormItem>
            <FormLabel>País</FormLabel>
            <SelectField
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                if (value !== 'Argentina') {
                  form.setValue('userData.state', '');
                }
              }}
              placeholder="Seleccionar país"
              triggerRef={field.ref}
            >
              {countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectField>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.state"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Provincia</FormLabel>
            {form.getValues('userData.country') === 'Argentina' ? (
              <SelectField
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Seleccionar provincia"
                triggerRef={field.ref}
              >
                {argStates.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectField>
            ) : (
              <Input placeholder="Provincia" {...field} />
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Ciudad</FormLabel>
            <FormControl>
              <Input placeholder="Ciudad" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.zipCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Código Postal</FormLabel>
            <FormControl>
              <Input placeholder="Código Postal" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
